import { Button, validation } from 'mypages-library';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReCaptcha } from 'components/common/ReCaptcha/ReCaptcha';
import { Input } from 'components/Input/Input';
import { useConfig } from 'common/hooks/useConfig';
import useNavigateOrClose from 'common/hooks/useNavigateOrClose';
import styles from 'common/PageTemplate/PageTemplate.module.scss';
import PageTemplate from 'common/PageTemplate/PageTemplate';

interface Props {
  onValidEmailEntered: (email: string, recaptchaResponse: string) => Promise<void>;
  setApiErrorMessage: (message: string) => void;
  apiErrorMessage: string;
}

const ForgotPasswordEnterEmail = ({ onValidEmailEntered, apiErrorMessage, setApiErrorMessage }: Props) => {
  const { isEmailValid } = validation;
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const emailError = email?.length > 0 && !isEmailValid(email);
  const emailErrorMsg = emailError ? t('MPL_CREATE_ACCOUNT_INVALID_EMAIL') : '';
  const [recaptchaResponse, setRecaptchaResponse] = useState<string | null>(null);
  const [captchaKey, setCaptchaKey] = useState<number>(0);

  const {
    config: { reCaptchaSiteKey },
  } = useConfig();

  const { navigateOrClose, runningInApp } = useNavigateOrClose();

  useEffect(() => {
    if (apiErrorMessage) {
      setCaptchaKey(prevKey => prevKey + 1);
      setRecaptchaResponse(null);
    }
  }, [apiErrorMessage]);

  return (
    <PageTemplate>
      <h1 className={styles.title}>{t('MPL_FORGOT_PW_TITLE')}</h1>
      <p className={styles.text}>{t('MPL_FORGOT_PW_INSTRUCTION')}</p>
      <Input
        type="email"
        inputMode="email"
        placeholder={t('MPL_LOGIN_PLACEHOLDER_EMAIL')}
        onChange={value => {
          setEmail(value.trim());
        }}
        value={email}
        maxLength={255}
        autoComplete="username"
        minLengthErrorText={t('MPL_NOT_ENOUGH_CHARACTERS_ERROR')}
        mandatoryInfoText={t('MPL_ETC_MANDATORY_INPUT_FIELD')}
        hasError={emailError}
        errorMessage={emailErrorMsg}
        dataTestId="forgotPasswordEmailInput"
      />
      <ReCaptcha
        key={captchaKey}
        onChange={res => {
          setRecaptchaResponse(res);
          setApiErrorMessage('');
        }}
        siteKey={reCaptchaSiteKey}
      />
      {apiErrorMessage && (
        <div id="forgotPasswordErrorMessage" className={styles.error}>
          {apiErrorMessage}
        </div>
      )}
      <Button
        disabled={loading || !email || !recaptchaResponse || emailError}
        text={t('MPL_FORGOT_PW_CHANGE_PASSWORD_BUTTON')}
        className={styles.confirmButton}
        dataTestId="forgotPasswordSubmitButton"
        red
        onClick={async () => {
          if (recaptchaResponse && email && !emailError) {
            setLoading(true);
            try {
              await onValidEmailEntered(email, recaptchaResponse);
            } finally {
              setLoading(false);
            }
          }
        }}
      />
      {!runningInApp && (
        <Button
          dataTestId="createAccountCancel"
          className={styles.cancelButton}
          text={t('MPL_CANCEL')}
          link
          onClick={() => navigateOrClose('/login')}
        />
      )}
    </PageTemplate>
  );
};
export default ForgotPasswordEnterEmail;
